import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/features/auth/use-auth";

function formatElapsed(ms: number): string {
	const totalSeconds = Math.max(0, Math.floor(ms / 1000));
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;
	if (hours > 0) {
		return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
	}
	return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export function ImpersonationBanner() {
	const { isImpersonating, impersonationStartedAt, user, stopImpersonation, logout } = useAuth();
	const navigate = useNavigate();
	const [elapsed, setElapsed] = useState<string | null>(null);

	useEffect(() => {
		if (!impersonationStartedAt) {
			setElapsed(null);
			return;
		}
		const tick = () => {
			setElapsed(formatElapsed(Date.now() - impersonationStartedAt));
		};
		tick();
		const interval = setInterval(tick, 1000);
		return () => clearInterval(interval);
	}, [impersonationStartedAt]);

	if (!isImpersonating) return null;

	const handleStop = async () => {
		try {
			await stopImpersonation();
			navigate({ to: "/dashboard" });
		} catch {
			await logout();
		}
	};

	return (
		<div className="fixed top-0 left-0 right-0 z-50 bg-[var(--status-warning-bg)] border-b border-[var(--status-warning-border)] px-4 py-2 flex items-center justify-between shadow-sm max-md:h-11 max-md:gap-2 max-md:py-0 max-md:pr-1.5 max-md:pl-3 max-md:shadow-none">
			<div className="flex items-center gap-2 max-md:min-w-0">
				<svg
					className="w-4 h-4 text-[var(--status-warning-text)] max-md:shrink-0"
					fill="none"
					viewBox="0 0 24 24"
					stroke="currentColor"
					aria-hidden="true"
					role="presentation"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
					/>
				</svg>
				<span className="text-sm font-medium text-[var(--status-warning-text)] max-md:truncate max-md:text-xs">
					Tryb podglądu — przeglądasz jako:{" "}
					<strong>
						{user?.name} ({user?.email})
					</strong>
				</span>
				{elapsed && (
					<span className="text-xs font-mono tabular-nums text-[var(--status-warning-text)] opacity-75 hidden sm:inline">
						({elapsed})
					</span>
				)}
			</div>
			<button
				type="button"
				onClick={handleStop}
				className="text-sm font-medium text-[var(--status-warning-text)] hover:underline focus:outline-none focus:ring-2 focus:ring-[var(--status-warning-text)] rounded px-2 max-md:h-9 max-md:shrink-0 max-md:rounded-md max-md:border max-md:border-[var(--status-warning-border)] max-md:text-xs"
			>
				Zakończ podgląd
			</button>
		</div>
	);
}
